<template>
  <LayoutSection v-if="hasDoctors">
    <div class="space-y-8">
      <div
        class="w-full"
        v-for="(doctor, index) in doctors"
        :key="getDoctorKey(doctor) || index"
      >
        <div
          class="relative bg-white-50 rounded-[2rem] p-4 sm:p-5 md:p-6 border border-[rgba(28,27,26,0.06)] md:max-w-none max-w-full doctor-card"
          @click="handleCardClick(doctor, $event)"
        >
          <button
            type="button"
            class="doctor-card__map"
            :aria-label="$t('View on map')"
            :title="$t('View on map')"
            @click.stop="handleViewMap(doctor)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="11"
                r="2.2"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </button>
          <div class="dc-card__header bg-white rounded-[1.6rem] px-4 sm:px-5 py-4">
            <DoctorInformation :doctor="doctor" :show-reviews="false" />
          </div>

          <p
            v-if="doctorAboutText(doctor)"
            class="dc-bio"
            :class="{ 'dc-bio--clamp': !isBioOpen(doctor) }"
          >
            {{ doctorAboutText(doctor) }}
          </p>
          <button
            v-if="doctorAboutText(doctor).length > 150"
            type="button"
            class="dc-bio__more"
            @click.stop="toggleBio(doctor)"
          >
            {{ isBioOpen(doctor) ? $t("Show less") : $t("Show more") }}
          </button>

          <BookingTypeSelector
            class="mt-4"
            :options="normalizedOptions(doctor)"
            :selected-key="resolveSelectedBookingKey(doctor)"
            @select="(key) => selectTypeByKey(doctor, key)"
          />

          <!-- Availability calendar for the SELECTED type -->
          <div v-if="selectedOptionFor(doctor)" class="dc-cal">
            <DoctorSchedule
              :key="getDoctorScheduleKey(doctor)"
              class="dc-cal__schedule"
              :doctor="doctor"
              :service-title="optLabel(selectedOptionFor(doctor))"
              :selected-booking-key="resolveSelectedBookingKey(doctor)"
              :dates-loading="datesLoading"
              :base-date="baseDate"
              :schedule-version="doctor.scheduleVersion || 0"
              @booking-option-change="
                (...args) => handleScheduleBookingOption(doctor, ...args)
              "
              @request-more-dates="handleRequestMoreDates(doctor, $event)"
              @request-calendar="handleRequestCalendar(doctor, $event)"
            />
          </div>
          <ClassCardsList
            v-if="doctor.classes && doctor.classes.length"
            :items="doctor.classes"
            :title="$t('Upcoming sessions')"
            list-class="classes-row"
            item-class="classes-row__item"
            wrapper-class="mt-6"
          >
            <template #cta="{ classData, navigate }">
              <div class="classes-row__cta">
                <ClassJoinButton
                  :class-data="classData"
                  :selected-slot="resolveDefaultSlot(classData)"
                  :label="$t('Book now')"
                  :block="true"
                />
                <AppButton
                  :label="$t('More information')"
                  variant="secondary"
                  class="class-card__more-button"
                  :block="true"
                  @click="navigate()"
                />
              </div>
            </template>
          </ClassCardsList>
        </div>
      </div>
    </div>
  </LayoutSection>

  <MapModal
    v-if="isMapModalVisible"
    :doctors="selectedDoctor"
    :dates-loading="datesLoading"
    :search-loading="searchLoading"
    :allow-map-search="allowMapSearch"
    :selected-booking-keys="selectedBookingKeys"
    @close="closeMapModal"
    @select-booking-option="handleBookingOptionSelection"
    @request-calendar="handleRequestCalendar"
    @request-more-dates="handleRequestMoreDates"
    @search-area="handleSearchArea"
  />

  <div
    v-if="showMinimap && hasDoctors"
    class="hidden lg:block fixed bottom-6 right-6 w-[230px] h-[230px] z-[12] rounded-[1.6rem] overflow-hidden shadow-lg border border-white bg-white"
  >
    <MapExpandButton
      class="absolute top-3 left-1/2 -translate-x-1/2 z-[2]"
      @click="openMapWithAllDoctors"
    />
    <GoogleMaps :doctors="doctors" class="w-full h-full" />
  </div>
</template>

<script>
import DoctorSchedule from "@/components/search/doctorSchedule.vue";
import DoctorInformation from "@/components/doctor/doctorInformation.vue";
import MapModal from "@/components/search/mapModal.vue";
import GoogleMaps from "@/components/doctor/googleMaps.vue";
import ClassCardsList from "@/components/classes/ClassCardsList.vue";
import ClassJoinButton from "@/components/classes/ClassJoinButton.vue";
import AppButton from "@/components/buttons/AppButton.vue";
import MapExpandButton from "@/components/buttons/MapExpandButton.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import BookingTypeSelector from "@/components/booking/bookingTypeSelector.vue";
import {
  getDoctorKey,
  hasCalendarSchedule,
  isScheduleCurrent,
  resolveSelectedBookingKey as resolveBookingSelection,
} from "@/helpers/doctorSelectionHelper";
import { buildDoctorProfileRoute } from "@/helpers/doctorRouteHelper";

export default {
  name: "SpecialistsAvailableSection",
  components: {
    MapExpandButton,
    AppButton,
    ClassJoinButton,
    DoctorInformation,
    DoctorSchedule,
    MapModal,
    ClassCardsList,
    LayoutSection,
    GoogleMaps,
    BookingTypeSelector,
  },
  props: {
    doctors: {
      type: Array,
      required: true,
    },
    datesLoading: {
      type: Boolean,
      default: false,
    },
    baseDate: {
      type: [Date, String],
      default: null,
    },
    searchLoading: {
      type: Boolean,
      default: false,
    },
    showMinimap: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["request-more-dates", "request-calendar", "search-area"],
  data() {
    return {
      selectedDoctor: [],
      isMapModalVisible: false,
      allowMapSearch: false,
      selectedBookingKeys: {},
      doctorCycleIds: {},
      bioOpenKeys: {},
    };
  },
  computed: {
    hasDoctors() {
      return Array.isArray(this.doctors) && this.doctors.length > 0;
    },
  },
  watch: {
    doctors: {
      handler(newDoctors) {
        if (!Array.isArray(newDoctors) || !newDoctors.length) {
          this.selectedBookingKeys = {};
          this.doctorCycleIds = {};
          if (this.isMapModalVisible && this.allowMapSearch) {
            this.selectedDoctor = [];
          }
          return;
        }

        const previousSelections = this.selectedBookingKeys || {};
        const previousCycles = this.doctorCycleIds || {};
        const nextSelections = {};
        const nextCycles = {};
        newDoctors.forEach((doctor) => {
          const doctorKey = this.getDoctorKey(doctor);
          if (!doctorKey) {
            return;
          }

          const rawCycle = doctor?.searchCycleId;
          const cycleToken = Number.isFinite(Number(rawCycle))
            ? Number(rawCycle)
            : rawCycle ?? null;
          nextCycles[doctorKey] = cycleToken;

          const options = Array.isArray(doctor?.bookingOptions)
            ? doctor.bookingOptions
            : [];
          const normalizedOptions = options
            .map((option) => {
              if (!option || option.key === undefined || option.key === null) {
                return null;
              }
              return {
                option,
                key: String(option.key),
              };
            })
            .filter(Boolean);

          if (!normalizedOptions.length) {
            return;
          }

          const resolvedKey = resolveBookingSelection(
            doctor,
            previousSelections,
          );
          const normalizedKey =
            resolvedKey !== null && resolvedKey !== undefined
              ? String(resolvedKey)
              : null;
          if (normalizedKey) {
            nextSelections[doctorKey] = normalizedKey;
          }

          const previousCycleToken = previousCycles[doctorKey] ?? null;
          const cycleChanged = previousCycleToken !== cycleToken;
          const targetKey = normalizedKey;

          if (targetKey && cycleChanged) {
            const selectedEntry =
              normalizedOptions.find((entry) => entry.key === targetKey) ||
              null;
            const selectedOption = selectedEntry ? selectedEntry.option : null;
            const scheduleLoading =
              selectedOption && selectedOption.scheduleLoading === true;
            const hasRealSchedule =
              selectedOption && hasCalendarSchedule(selectedOption);
            const shouldFetch =
              !this.datesLoading &&
              !scheduleLoading &&
              (!hasRealSchedule || !isScheduleCurrent(doctor, selectedOption));

            if (shouldFetch) {
              this.$emit("request-calendar", doctor, {
                bookingKey: targetKey,
                date: doctor?.baseDateIso || this.baseDate || null,
                force: true,
              });
            }
          }
        });

        this.selectedBookingKeys = nextSelections;
        this.doctorCycleIds = nextCycles;

        if (this.isMapModalVisible && this.allowMapSearch) {
          this.selectedDoctor = newDoctors;
        } else if (
          this.isMapModalVisible &&
          Array.isArray(this.selectedDoctor) &&
          this.selectedDoctor.length
        ) {
          const selectedKeys = this.selectedDoctor
            .map((doctor) => this.getDoctorKey(doctor))
            .filter((key) => key !== null && key !== undefined);

          if (selectedKeys.length) {
            const nextSelected = selectedKeys
              .map((key) =>
                newDoctors.find((entry) => this.getDoctorKey(entry) === key),
              )
              .filter((entry) => entry);

            if (nextSelected.length) {
              this.selectedDoctor = nextSelected;
            }
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    doctorOptions(doctor) {
      const opts = Array.isArray(doctor?.bookingOptions)
        ? doctor.bookingOptions
        : [];
      const unique = new Map();
      opts.forEach((option) => {
        const rawKey = option.key ?? option.bookingTypeId ?? option.label;
        const k = String(rawKey);
        if (!unique.has(k)) {
          unique.set(k, { ...option, key: k });
        }
      });
      return Array.from(unique.values());
    },
    selectedOptionFor(doctor) {
      const opts = this.doctorOptions(doctor);
      if (!opts.length) {
        return null;
      }
      const key = this.resolveSelectedBookingKey(doctor);
      const nk = key !== null && key !== undefined ? String(key) : null;
      return opts.find((o) => o.key === nk) || opts[0];
    },
    optLabel(option) {
      return (
        (option && (option.bookingTypeName || option.label)) ||
        this.$t("Service")
      );
    },
    optPrice(option) {
      return (option && option.price) || "";
    },
    optDesc(option) {
      return (option && option.bookingTypeDescription) || "";
    },
    optImage(option) {
      return (option && option.bookingTypeImage) || "";
    },
    normalizedOptions(doctor) {
      // spread first so new option fields reach the selector without touching this map
      return this.doctorOptions(doctor).map((option) => ({
        ...option,
        key: option.key,
        label: this.optLabel(option),
        price: this.optPrice(option),
        description: this.optDesc(option),
        image: this.optImage(option),
      }));
    },
    doctorAboutText(doctor) {
      const a = doctor && doctor.aboutMe;
      if (Array.isArray(a)) {
        return a.filter(Boolean).join(" ").trim();
      }
      return typeof a === "string" ? a.trim() : "";
    },
    isBioOpen(doctor) {
      return !!this.bioOpenKeys[this.getDoctorKey(doctor)];
    },
    toggleBio(doctor) {
      const dk = this.getDoctorKey(doctor);
      this.bioOpenKeys = { ...this.bioOpenKeys, [dk]: !this.bioOpenKeys[dk] };
    },
    selectTypeByKey(doctor, key) {
      this.handleBookingOptionSelection(doctor, key);
    },
    handleCardClick(doctor, event) {
      // Don't navigate when the click lands on an interactive control
      // (booking radios, schedule times, map button, links, sliders).
      if (
        event.target.closest(
          'button, a, input, select, textarea, [role="radio"], [role="option"], .doctor-schedule, .bts__list',
        )
      ) {
        return;
      }
      const route = buildDoctorProfileRoute(doctor);
      if (route) {
        this.$router.push(route);
      }
    },
    handleViewMap(doctor) {
      this.selectedDoctor = [doctor];
      this.allowMapSearch = false;
      this.isMapModalVisible = true;
    },
    openMapWithAllDoctors() {
      this.selectedDoctor = this.doctors;
      this.allowMapSearch = true;
      this.isMapModalVisible = true;
    },
    closeMapModal() {
      this.isMapModalVisible = false;
      this.allowMapSearch = false;
    },
    handleSearchArea(payload) {
      this.$emit("search-area", payload);
    },
    resolveSelectedBookingKey(doctor) {
      return resolveBookingSelection(doctor, this.selectedBookingKeys);
    },
    getDoctorScheduleKey(doctor) {
      const doctorKey = getDoctorKey(doctor) || "doctor";
      const bookingKey = this.resolveSelectedBookingKey(doctor) || "default";
      return `${doctorKey}::${bookingKey}`;
    },
    handleBookingOptionSelection(doctor, bookingKey, context) {
      const doctorKey = getDoctorKey(doctor);
      if (!doctorKey) {
        return;
      }
      const normalizedKey =
        bookingKey !== null && bookingKey !== undefined
          ? String(bookingKey)
          : null;
      if (!normalizedKey) {
        return;
      }
      this.selectedBookingKeys = {
        ...this.selectedBookingKeys,
        [doctorKey]: normalizedKey,
      };
      const options = Array.isArray(doctor?.bookingOptions)
        ? doctor.bookingOptions
        : [];
      const option =
        options.find((entry) => entry && String(entry.key) === normalizedKey) ||
        null;
      const isInit =
        context && (context.source === "init" || context.source === "prop");
      if (
        isInit &&
        option &&
        option.scheduleLoaded &&
        hasCalendarSchedule(option) &&
        isScheduleCurrent(doctor, option)
      ) {
        return;
      }
      if (option && option.scheduleLoading) {
        return;
      }
      const baseDate = doctor?.baseDateIso || this.baseDate || null;
      this.$emit("request-calendar", doctor, {
        bookingKey: normalizedKey,
        date: baseDate,
        force: true,
      });
    },
    handleScheduleBookingOption(doctor, bookingKey, context) {
      this.handleBookingOptionSelection(doctor, bookingKey, context);
    },
    handleRequestMoreDates(doctor, payload) {
      this.$emit("request-more-dates", doctor, payload);
    },
    handleRequestCalendar(doctor, payload) {
      this.$emit("request-calendar", doctor, payload);
    },
    resolveDefaultSlot(classItem) {
      const slots = Array.isArray(classItem?.bookings)
        ? classItem.bookings
        : [];
      return slots.length ? slots[0] : null;
    },
    getDoctorKey,
  },
};
</script>

<style scoped>
.doctor-card {
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
  box-shadow: 0 1px 2px rgba(28, 27, 26, 0.03);
}

.doctor-card:hover {
  /* warm tinted shadow, not generic pure black */
  box-shadow: 0 16px 40px rgba(28, 27, 26, 0.09);
  border-color: rgba(91, 111, 238, 0.18);
  transform: translateY(-2px);
}

/* Interactive regions keep the default cursor (clicking them does not navigate) */
.doctor-card :deep(.doctor-schedule),
.doctor-card :deep(.bts__list) {
  cursor: default;
}

/* ---- Header ---- */
.dc-card__header {
  box-shadow: 0 1px 2px rgba(28, 27, 26, 0.03);
}

/* ---- Compact result card: professional bio + scrollable service catalog ---- */
.dc-bio {
  margin-top: 1.2rem;
  font-size: 1.25rem;
  line-height: 1.55;
  color: #4b5563;
}

.dc-bio--clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.dc-bio__more {
  margin-top: 0.3rem;
  padding: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #5b6fee;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.dc-bio__more:hover {
  color: #3346d3;
  text-decoration: underline;
}

/* Availability calendar, clearly tied to the selected service type */
.dc-cal {
  margin-top: 0.6rem;
}


/* Map shortcut, top-right corner. Crisp inline pin, no bitmap scaling. */
.doctor-card__map {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 999px;
  border: 1px solid rgba(28, 27, 26, 0.1);
  background: #ffffff;
  color: #3346d3;
  box-shadow: 0 2px 8px rgba(28, 27, 26, 0.06);
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
}

.doctor-card__map svg {
  width: 2rem;
  height: 2rem;
}

.doctor-card__map:hover {
  background: rgba(91, 111, 238, 0.08);
  border-color: rgba(91, 111, 238, 0.3);
}

.doctor-card__map:active {
  transform: translateY(1px);
}

.classes-row__cta {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.classes-row__cta :deep(.app-button) {
  width: 100%;
}

.map-results-button {
  border-color: #ffffff;
  background: #ffffff;
  box-shadow: 0 2.067px 17.567px rgba(193, 193, 193, 0.25);
  backdrop-filter: blur(1.9px);
}

</style>
